import apiService from './apiService';
import { API_BASE_URL } from '../config/api';
import { authFetch } from './session';

export interface RegulatoryRequirementsResponse {
  success: boolean;
  country: string;
  number_type: string;
  end_user_type: string;
  regulations: unknown[];
}

export interface RegulatoryStatusResponse {
  success: boolean;
  has_bundle: boolean;
  bundle_sid?: string;
  status: string | null;
  approved?: boolean;
  /** German, per-document reasons why Twilio's review rejected the bundle. */
  rejection_reasons?: string[];
}

export interface RegulatoryBundleSubmitResponse {
  success: boolean;
  bundle_sid: string;
  status: string;
}

export interface TwilioAvailableNumber {
  phone_number: string;
  friendly_name?: string;
  region?: string;
  locality?: string;
  iso_country?: string;
  address_requirements?: string;
  capabilities?: Record<string, any>;
  /** Gesetzt, wenn der Nummerntyp vom gesuchten abweicht — z. B. AT-Fallback
   *  auf österreichweite +43 720 (national), wenn das Ortsnetz leer ist.
   *  Der Kauf muss dann mit DIESEM Typ erfolgen. */
  number_type?: string | null;
}

export interface TwilioSmsMessage {
  from_number?: string;
  to_number?: string;
  body?: string;
  sms_sid?: string;
  received_at?: string;
}

export interface TwilioAvailableNumbersResponse {
  success: boolean;
  numbers: TwilioAvailableNumber[];
  /** Erklärung, wenn eine leere Liste erwartbar ist (z. B. kein Twilio-Bestand im eigenen AT-Ortsnetz). */
  hint?: string | null;
}

export interface TwilioPurchaseNumberResponse {
  success: boolean;
  phone_number: string;
  phone_sid?: string;
  sms_webhook_url?: string;
}

export interface TwilioSmsListResponse {
  success: boolean;
  messages: TwilioSmsMessage[];
  limit: number;
}

export interface TwilioLatestNumberResponse {
  success: boolean;
  phone_number?: string | null;
  phone_sid?: string | null;
}

export interface TwilioFilterOption {
  value: string;
  label: string;
  region?: string;
}

export interface TwilioFilterOptionsResponse {
  success: boolean;
  regions: TwilioFilterOption[];
  localities: TwilioFilterOption[];
}

export interface TwilioOnboardingChoiceRequest {
  wants_twilio_number: boolean;
  has_own_number?: boolean;
  own_phone_number?: string;
  region?: string;
  locality?: string;
}

// Voice-related interfaces
export interface TwilioVoiceStatusResponse {
  success: boolean;
  has_number: boolean;
  phone_number?: string;
  voice_enabled?: boolean;
  voice_webhook_url?: string;
  twilio_capabilities?: {
    phone_number?: string;
    voice_enabled?: boolean;
    voice_url?: string;
  };
}

export interface TwilioVoiceToggleResponse {
  success: boolean;
  voice_enabled: boolean;
  phone_number?: string;
  voice_webhook_url?: string;
}

export interface TwilioWebhookConfigResponse {
  success: boolean;
  phone_number?: string;
  voice_webhook_url?: string;
  message?: string;
  error?: string;
}

export interface TwilioVoiceCall {
  call_sid: string;
  from_number?: string;
  to_number?: string;
  direction?: string;
  status?: string;
  duration?: number;
  started_at?: string;
  ended_at?: string;
  caller_city?: string;
  caller_country?: string;
}

export interface TwilioVoiceCallsResponse {
  success: boolean;
  calls: TwilioVoiceCall[];
  limit: number;
}

// Voice Verification interfaces (for WhatsApp with landline numbers)
export interface TwilioVoiceVerificationRequest {
  success: boolean;
  message: string;
  phone_number_id: string;
  twilio_number: string;
  verification_id: number;
  expires_at: string;
  poll_endpoint: string;
}

export interface TwilioVerificationCodeResponse {
  success: boolean;
  pending?: boolean;
  code?: string;
  received_at?: string;
  expired?: boolean;
  verified?: boolean;
  message?: string;
  error?: string;
  expires_at?: string;
  recording_url?: string;
  raw_transcript?: string;
}

// Number type for selection
export type TwilioNumberType = 'local' | 'mobile';

const twilioService = {
  listAvailableNumbers: async (params: {
    number_type?: TwilioNumberType;
    region?: string;
    locality?: string;
    contains?: string;
    limit?: number;
    country?: string;
  }): Promise<TwilioAvailableNumbersResponse> => {
    const query = new URLSearchParams();
    if (params.number_type) query.set('number_type', params.number_type);
    if (params.region) query.set('region', params.region);
    if (params.locality) query.set('locality', params.locality);
    if (params.contains) query.set('contains', params.contains);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.country) query.set('country', params.country);

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiService.get<TwilioAvailableNumbersResponse>(`/twilio/available-numbers${suffix}`);
  },

  purchaseNumber: async (data: {
    phone_number: string;
    region?: string;
    locality?: string;
    number_type?: 'local' | 'mobile' | 'tollfree' | 'national';
    /**
     * "additional_agent" buys a further voice number for a location-scoped
     * agent: it keeps the tenant's primary-number slot and skips the WhatsApp
     * voice verification webhook. Defaults to the onboarding purchase.
     */
    purpose?: 'onboarding' | 'additional_agent';
  }): Promise<TwilioPurchaseNumberResponse> => {
    return apiService.post<TwilioPurchaseNumberResponse>('/twilio/purchase-number', data);
  },

  // --- Regulatory Compliance (CH/AT self-service bundles) -------------------
  getRegulatoryRequirements: async (
    country: string,
    numberType: string = 'local',
    endUserType: string = 'business',
  ): Promise<RegulatoryRequirementsResponse> => {
    const q = new URLSearchParams({ country, number_type: numberType, end_user_type: endUserType });
    return apiService.get<RegulatoryRequirementsResponse>(`/twilio/regulatory/requirements?${q.toString()}`);
  },

  getRegulatoryStatus: async (
    country: string,
    numberType: string = 'local',
  ): Promise<RegulatoryStatusResponse> => {
    const q = new URLSearchParams({ country, number_type: numberType });
    return apiService.get<RegulatoryStatusResponse>(`/twilio/regulatory/status?${q.toString()}`);
  },

  // Multipart submit (FormData with the document file) — apiService always sets
  // JSON content-type, so we use a raw fetch and let the browser set the boundary.
  submitRegulatoryBundle: async (form: FormData): Promise<RegulatoryBundleSubmitResponse> => {
    const res = await authFetch(`${API_BASE_URL}/twilio/regulatory/bundle`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      let detail = 'Bundle-Einreichung fehlgeschlagen';
      try {
        const j = await res.json();
        detail = (j && (j.detail || j.message)) || detail;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(detail);
    }
    return res.json();
  },

  setOnboardingChoice: async (data: TwilioOnboardingChoiceRequest): Promise<{ success: boolean }> => {
    return apiService.post<{ success: boolean }>('/twilio/onboarding-choice', data);
  },

  /**
   * @param toNumber Restrict to SMS addressed to this line. Required once the
   *   account has more than one number, otherwise another line's code shows up.
   */
  getOnboardingSms: async (limit: number, toNumber?: string): Promise<TwilioSmsListResponse> => {
    const query = new URLSearchParams({ limit: String(limit) });
    if (toNumber) query.set('to_number', toNumber);
    return apiService.get<TwilioSmsListResponse>(`/twilio/onboarding-sms?${query.toString()}`);
  },

  getFilterOptions: async (): Promise<TwilioFilterOptionsResponse> => {
    return apiService.get<TwilioFilterOptionsResponse>('/twilio/filter-options');
  },

  getLatestNumber: async (): Promise<TwilioLatestNumberResponse> => {
    return apiService.get<TwilioLatestNumberResponse>('/twilio/latest-number');
  },

  // Voice-related methods
  getVoiceStatus: async (): Promise<TwilioVoiceStatusResponse> => {
    return apiService.get<TwilioVoiceStatusResponse>('/twilio/voice-status');
  },

  enableVoice: async (): Promise<TwilioVoiceToggleResponse> => {
    return apiService.post<TwilioVoiceToggleResponse>('/twilio/enable-voice', {});
  },

  disableVoice: async (): Promise<TwilioVoiceToggleResponse> => {
    return apiService.post<TwilioVoiceToggleResponse>('/twilio/disable-voice', {});
  },

  getVoiceCalls: async (limit: number = 20): Promise<TwilioVoiceCallsResponse> => {
    return apiService.get<TwilioVoiceCallsResponse>(`/twilio/voice-calls?limit=${limit}`);
  },

  // Voice Verification methods (for WhatsApp with landline numbers)
  requestVoiceVerification: async (phoneNumberId: string): Promise<TwilioVoiceVerificationRequest> => {
    return apiService.post<TwilioVoiceVerificationRequest>(
      `/twilio/request-voice-verification?phone_number_id=${encodeURIComponent(phoneNumberId)}`,
      {}
    );
  },

  /**
   * @param twilioNumber Restrict to verifications of this line. Required once
   *   the account connects a second WhatsApp number, otherwise the newest code
   *   of any line would be shown.
   */
  getVerificationCode: async (
    phoneNumberId?: string,
    twilioNumber?: string,
  ): Promise<TwilioVerificationCodeResponse> => {
    const query = new URLSearchParams();
    if (phoneNumberId) query.set('phone_number_id', phoneNumberId);
    if (twilioNumber) query.set('twilio_number', twilioNumber);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiService.get<TwilioVerificationCodeResponse>(`/twilio/verification-code${suffix}`);
  },

  // Route Meta's verification call to the transcribing webhook instead of the
  // voice agent. Without phoneNumber this targets the tenant's primary number
  // (onboarding); pass one to put exactly that agent line into verification.
  configureVerificationWebhook: async (phoneNumber?: string): Promise<TwilioWebhookConfigResponse> => {
    return apiService.post('/twilio/configure-verification-webhook',
      phoneNumber ? { phone_number: phoneNumber } : {});
  },

  // Hand the line back to the voice agent after the verification is done.
  configureVoiceWebhook: async (phoneNumber?: string): Promise<TwilioWebhookConfigResponse> => {
    return apiService.post('/twilio/configure-voice-webhook',
      phoneNumber ? { phone_number: phoneNumber } : {});
  },

  // Finalize onboarding (save preferences + reset webhook if needed)
  finalizeOnboarding: async (data: {
    wants_phone_agent?: boolean;
    wants_whatsapp?: boolean;
    whatsapp_use_reserved_number?: boolean;
  }): Promise<{ success: boolean; webhook_reset_to_voice_agent: boolean }> => {
    return apiService.post('/twilio/finalize-onboarding', data);
  },

  // Number Release methods
  getReleasePreview: async (): Promise<TwilioReleasePreviewResponse> => {
    return apiService.get<TwilioReleasePreviewResponse>('/twilio/release-preview');
  },

  releaseNumber: async (confirm: boolean = true): Promise<TwilioReleaseResponse> => {
    return apiService.post<TwilioReleaseResponse>(`/twilio/release-number?confirm=${confirm}`, {});
  },
};

// Release-related interfaces
export interface TwilioReleasePreviewResponse {
  success: boolean;
  has_number: boolean;
  phone_number?: string;
  voice_enabled?: boolean;
  whatsapp_connected?: boolean;
  data_to_delete?: {
    sms_messages: number;
    voice_calls: number;
    verifications: number;
  };
  warning?: string;
  message?: string;
}

export interface TwilioReleaseResponse {
  success: boolean;
  message: string;
  released_number?: string;
  cleanup_summary?: {
    twilio_number_released: boolean;
    whatsapp_disconnected: boolean;
    sms_deleted: number;
    calls_deleted: number;
    verifications_deleted: number;
  };
}

export default twilioService;
